import { t } from 'i18next';
import Quill from 'quill';
import type React from 'react';

// Helper function to close all open tooltips
const closeAllTooltips = () => {
  const selectors = [
    '.quill-tooltip-input-container',
    '.custom-tooltip-edit-box',
    '.custom-tooltip-hover-display',
  ];
  selectors.forEach((selector) => {
    const elements = document.body.querySelectorAll(selector);
    elements.forEach((element) => {
      if (!element.isConnected) return;

      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA') &&
        element.contains(activeElement)
      ) {
        // Tooltip is being edited, don't close it
        return;
      }

      const parent = element.parentNode;
      if (!parent) return;

      if (!parent.contains(element)) return;

      try {
        parent.removeChild(element);
      } catch (error) {
        // Already removed
      }
    });
  });
};

// Check if Quill container is visible
const checkQuillVisibility = (quillContainer: HTMLElement) => {
  const rect = quillContainer.getBoundingClientRect();
  const isVisible =
    rect.width > 0 &&
    rect.height > 0 &&
    rect.top < window.innerHeight &&
    rect.bottom > 0;

  if (!isVisible) {
    closeAllTooltips();
  }
};

// Custom TooltipBlot for Quill
const Inline = Quill.import('blots/inline');
class TooltipBlot extends Inline {
  static create(value: string) {
    const node = super.create();
    node.setAttribute('data-quill-tooltip', value);
    node.classList.add('custom-tooltip');
    return node;
  }
  static formats(node: HTMLElement) {
    return node.getAttribute('data-quill-tooltip');
  }
  format(name: string, value: string | boolean) {
    if (name === 'tooltip') {
      if (value && typeof value === 'string') {
        this.domNode.setAttribute('data-quill-tooltip', value);
      } else {
        this.domNode.removeAttribute('data-quill-tooltip');
        this.domNode.classList.remove('custom-tooltip');
        const attributeCount = this.domNode.attributes.length;
        const classCount = this.domNode.classList.length;

        if (attributeCount === 0 && classCount === 0) {
          super.format(name, false);
        }
      }
    } else {
      super.format(name, value);
    }
  }
}
TooltipBlot.blotName = 'tooltip';
TooltipBlot.tagName = 'span';
Quill.register(TooltipBlot);

const isMultilineSelection = (
  quill: any,
  index: number,
  length: number,
): boolean => {
  const bounds = quill.getBounds(index, length);
  const singleCharBounds = quill.getBounds(index, 1);
  return bounds.height > singleCharBounds.height * 1.5;
};

const getFirstWordRect = (
  quill: any,
  quillContainer: HTMLElement,
  index: number,
  length: number,
): { top: number; left: number; width: number; height: number } | null => {
  const text = quill.getText(index, length);
  const words = text.trim().split(/\s+/);

  if (words.length === 0 || words[0].length === 0) {
    return null;
  }

  const firstWord = words[0];
  const firstWordIndex = index + text.indexOf(firstWord);
  const firstWordLength = firstWord.length;

  const bounds = quill.getBounds(firstWordIndex, firstWordLength);
  const containerRect = quillContainer.getBoundingClientRect();

  return {
    top: containerRect.top + bounds.top,
    left: containerRect.left + bounds.left,
    width: bounds.width,
    height: bounds.height,
  };
};

// Helper function to check if an element or selection is inside a container with 'summary' class
const isInsideSummaryContainer = (
  elementOrQuill: HTMLElement | { quill: any; index: number; length: number },
): boolean => {
  let element: HTMLElement | null = null;

  if ('quill' in elementOrQuill) {
    const { quill } = elementOrQuill;
    element = quill.container;
  } else {
    element = elementOrQuill;
  }

  if (!element) return false;

  let current: HTMLElement | null = element;

  while (current) {
    if (
      current.classList &&
      (current.classList.contains('quill-editor-summary') ||
        current.classList.contains('quill-summary'))
    ) {
      return true;
    }
    current = current.parentElement;
  }

  return false;
};

const getTargetRectViewportForQuill = (
  quill: any,
  quillContainer: HTMLElement,
  index: number,
  length: number,
  bounds: { top: number; left: number; width: number; height: number },
): { top: number; left: number; width: number; height: number } => {
  if (isMultilineSelection(quill, index, length)) {
    const firstWordRect = getFirstWordRect(
      quill,
      quillContainer,
      index,
      length,
    );
    if (firstWordRect) {
      return firstWordRect;
    }
  }
  const containerRect = quillContainer.getBoundingClientRect();

  return {
    top: containerRect.top + bounds.top,
    left: containerRect.left + bounds.left,
    width: bounds.width,
    height: bounds.height,
  };
};

// Helper function to get target rect viewport for HTML element
const getTargetRectViewportForElement = (
  element: HTMLElement,
): {
  top: number;
  left: number;
  width: number;
  height: number;
} => {
  const rect = element.getBoundingClientRect();
  if (isMultilineElement(element)) {
    const firstWordRect = getFirstWordRectForElement(element);
    if (firstWordRect) {
      return firstWordRect;
    }
  }
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
};

// Helper function to remove existing tooltips
const removeExistingTooltips = () => {
  const existing = document.body.querySelectorAll(
    '.quill-tooltip-input-container, .custom-tooltip-edit-box, .custom-tooltip-hover-display',
  );

  existing.forEach((element) => {
    if (!element.isConnected) return;

    const parent = element.parentNode;
    if (!parent) return;

    if (!parent.contains(element)) return;

    try {
      parent.removeChild(element);
    } catch (error) {
      // Already removed
    }
  });
};

const createTooltipContainer = (
  className: string,
  isInSummary: boolean,
): HTMLElement => {
  const container = document.createElement('div');
  container.className = `${className} quill-tooltip-container${
    isInSummary ? ' white' : ''
  }`;
  return container;
};

const addCaretToTooltip = (container: HTMLElement) => {
  const caret = document.createElement('div');
  caret.className = 'quill-tooltip-caret';
  container.appendChild(caret);
};

const positionAndStyleTooltip = (
  tooltipBox: HTMLElement,
  targetRectViewport: {
    top: number;
    left: number;
    width: number;
    height: number;
  },
) => {
  const { top, left } = positionTooltip(tooltipBox, targetRectViewport);
  tooltipBox.style.left = `${left}px`;
  tooltipBox.style.top = `${top}px`;
  tooltipBox.style.transform = 'translateX(-50%)';
};

// Helper function to create auto-resize textarea
const createAutoResizeTextarea = (
  value: string,
  placeholder: string,
  onResize?: () => void,
): HTMLTextAreaElement => {
  const textarea = document.createElement('textarea');
  textarea.className = 'quill-tooltip-input';
  textarea.rows = 1;
  textarea.style.overflow = 'hidden';
  textarea.value = value;
  textarea.setAttribute('placeholder', placeholder);

  const resize = () => {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
    if (onResize) onResize();
  };

  textarea.addEventListener('input', resize);
  setTimeout(resize, 0);
  return textarea;
};

// Helper function to position tooltip above the text
// Returns position relative to viewport
export const positionTooltip = (
  tooltipBox: HTMLElement,
  targetRectViewport: {
    top: number;
    left: number;
    width: number;
    height: number;
  },
  gap = 8,
): { top: number; left: number } => {
  const wasAppended = tooltipBox.parentElement === document.body;
  if (!wasAppended) {
    tooltipBox.style.visibility = 'hidden';
    document.body.appendChild(tooltipBox);
  }

  const tooltipHeight = tooltipBox.offsetHeight;
  const targetTop = targetRectViewport.top;

  const actualGap = Math.max(gap, 12);
  const top = targetTop - tooltipHeight - actualGap + 6;
  const left = targetRectViewport.left + targetRectViewport.width / 2;

  if (!wasAppended) {
    const parent = tooltipBox.parentNode;
    if (parent && parent.contains(tooltipBox)) {
      try {
        parent.removeChild(tooltipBox);
      } catch (error) {
        // Already removed
      }
    }
    tooltipBox.style.visibility = '';
  }

  return { top, left };
};

export const setupTooltipButton = (quillRef: React.RefObject<any>) => {
  const tooltipButton = document.getElementById(
    'custom-tooltip-btn',
  ) as HTMLButtonElement;
  if (tooltipButton) {
    tooltipButton.onclick = () => {
      const quill = quillRef.current;
      if (!quill) return;
      const range = quill.getSelection();
      if (range && range.length > 0) {
        const finalRange = range;
        const bounds = quill.getBounds(finalRange.index, finalRange.length);
        const quillContainer = quill.container;
        removeExistingTooltips();

        const isInSummary = isInsideSummaryContainer({
          quill,
          index: finalRange.index,
          length: finalRange.length,
        });

        const tooltipBox = createTooltipContainer(
          'quill-tooltip-input-container',
          isInSummary,
        );

        const targetRectViewport = getTargetRectViewportForQuill(
          quill,
          quillContainer,
          finalRange.index,
          finalRange.length,
          bounds,
        );

        const tooltipTextarea = createAutoResizeTextarea(
          '',
          t('generic.enterTooltipText'),
          () => {
            positionAndStyleTooltip(tooltipBox, targetRectViewport);
          },
        );
        tooltipBox.appendChild(tooltipTextarea);
        addCaretToTooltip(tooltipBox);
        document.body.appendChild(tooltipBox);

        positionAndStyleTooltip(tooltipBox, targetRectViewport);
        tooltipTextarea.focus();

        let isApplied = false;
        const applyTooltip = () => {
          if (isApplied) return;
          isApplied = true;

          const tooltipText = tooltipTextarea.value.trim();

          if (tooltipBox.isConnected) {
            tooltipTextarea.removeEventListener('keydown', keydownHandler);
            tooltipTextarea.removeEventListener('blur', applyTooltip);
            const parent = tooltipBox.parentNode;
            if (parent && parent.contains(tooltipBox)) {
              try {
                parent.removeChild(tooltipBox);
              } catch (error) {
                // Already removed
              }
            }
          }

          const allBlots: any[] = [];
          quill.scroll.descendants((blot: any) => {
            if (blot.statics.blotName === 'tooltip') {
              allBlots.push(blot);
            }
          });

          // Collect tooltips to remove
          const tooltipsToRemove: Array<{
            index: number;
            length: number;
            text: string;
          }> = [];

          // Remove tooltips in reverse order
          tooltipsToRemove
            .sort((a, b) => b.index - a.index)
            .forEach(({ index, length, text }) => {
              quill.setSelection(index, length);
              quill.deleteText(index, length);
              quill.insertText(index, text);
            });

          quill.setSelection(finalRange.index, finalRange.length);

          if (tooltipText) {
            quill.format('tooltip', tooltipText);
          } else {
            quill.setSelection(finalRange.index, finalRange.length);
          }

          quill.focus();
        };

        const keydownHandler = (e: KeyboardEvent) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            applyTooltip();
          } else if (e.key === 'Escape') {
            if (isApplied) return;
            isApplied = true;

            if (tooltipBox.isConnected) {
              tooltipTextarea.removeEventListener('keydown', keydownHandler);
              tooltipTextarea.removeEventListener('blur', applyTooltip);
              const parent = tooltipBox.parentNode;
              if (parent && parent.contains(tooltipBox)) {
                parent.removeChild(tooltipBox);
              }
            }
            quill.setSelection(range.index, range.length);
            quill.focus();
          }
        };

        tooltipTextarea.addEventListener('keydown', keydownHandler);
        tooltipTextarea.addEventListener('blur', applyTooltip);
      }
    };
  }
};

// Show tooltip on hover (display only)
const handleTooltipHover = (
  quill: any,
  quillContainer: HTMLElement,
  e: Event,
) => {
  const target = e.target as HTMLElement;
  if (
    target &&
    target.classList.contains('custom-tooltip') &&
    target.hasAttribute('data-quill-tooltip')
  ) {
    const existingEditBox = document.body.querySelector(
      '.custom-tooltip-edit-box',
    );
    if (existingEditBox) return;

    // Remove any existing hover tooltip
    const existingHover = document.body.querySelector(
      '.custom-tooltip-hover-display',
    );
    if (existingHover && existingHover.isConnected) {
      const parent = existingHover.parentNode;
      if (parent && parent.contains(existingHover)) {
        try {
          parent.removeChild(existingHover);
        } catch (error) {
          // Already removed
        }
      }
    }

    const tooltipText = target.getAttribute('data-quill-tooltip') || '';
    if (!tooltipText) return;

    const isInSummary = isInsideSummaryContainer(target);
    const hoverTooltip = createTooltipContainer(
      'custom-tooltip-hover-display',
      isInSummary,
    );
    hoverTooltip.textContent = tooltipText;
    addCaretToTooltip(hoverTooltip);
    document.body.appendChild(hoverTooltip);

    // Get target rect viewport
    let targetRectViewport: {
      top: number;
      left: number;
      width: number;
      height: number;
    };
    const blot = Quill.find(target);
    if (blot) {
      const index = blot.offset(quill.scroll);
      const length = blot.length();
      const bounds = quill.getBounds(index, length);
      targetRectViewport = getTargetRectViewportForQuill(
        quill,
        quillContainer,
        index,
        length,
        bounds,
      );
    } else {
      targetRectViewport = getTargetRectViewportForElement(target);
    }

    positionAndStyleTooltip(hoverTooltip, targetRectViewport);

    // Remove on mouse leave from both target and tooltip
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const removeHover = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      timeoutId = setTimeout(() => {
        if (hoverTooltip.isConnected) {
          const parent = hoverTooltip.parentNode;
          if (parent && parent.contains(hoverTooltip)) {
            try {
              parent.removeChild(hoverTooltip);
            } catch (error) {
              // Already removed
            }
          }
          target.removeEventListener('mouseleave', removeHover);
          hoverTooltip.removeEventListener('mouseenter', cancelRemove);
          hoverTooltip.removeEventListener('mouseleave', removeHover);
        }
      }, 100);
    };

    const cancelRemove = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    target.addEventListener('mouseleave', removeHover);
    hoverTooltip.addEventListener('mouseenter', cancelRemove);
    hoverTooltip.addEventListener('mouseleave', removeHover);
  }
};

// Show tooltip edit only on click
const handleTooltipClick = (
  quill: any,
  quillContainer: HTMLElement,
  e: Event,
) => {
  // Save the click coordinates
  const clickX = (e as MouseEvent).clientX;
  const clickY = (e as MouseEvent).clientY;

  setTimeout(() => {
    const newTarget = document.elementFromPoint(clickX, clickY) as HTMLElement;
    if (
      newTarget &&
      newTarget.classList.contains('custom-tooltip') &&
      newTarget.hasAttribute('data-quill-tooltip')
    ) {
      removeExistingTooltips();

      const isInSummary = isInsideSummaryContainer(newTarget);
      const tooltipBox = createTooltipContainer(
        'custom-tooltip-edit-box',
        isInSummary,
      );

      let targetRectViewport: {
        top: number;
        left: number;
        width: number;
        height: number;
      };

      const blot = Quill.find(newTarget);

      if (blot) {
        const index = blot.offset(quill.scroll);
        const length = blot.length();
        const bounds = quill.getBounds(index, length);
        targetRectViewport = getTargetRectViewportForQuill(
          quill,
          quillContainer,
          index,
          length,
          bounds,
        );
      } else {
        targetRectViewport = getTargetRectViewportForElement(newTarget);
      }

      const textarea = createAutoResizeTextarea(
        newTarget.getAttribute('data-quill-tooltip') || '',
        '',
        () => {
          positionAndStyleTooltip(tooltipBox, targetRectViewport);
        },
      );

      tooltipBox.appendChild(textarea);
      addCaretToTooltip(tooltipBox);
      document.body.appendChild(tooltipBox);

      positionAndStyleTooltip(tooltipBox, targetRectViewport);
      textarea.focus();
      textarea.select();

      let isSaved = false;
      const saveTooltip = () => {
        if (isSaved) return;
        isSaved = true;

        const newTooltip = textarea.value.trim();
        const blot = Quill.find(newTarget);
        if (blot) {
          const index = blot.offset(quill.scroll);
          const length = blot.length();

          if (newTooltip) {
            quill.formatText(index, length, 'tooltip', newTooltip);
          } else {
            const textContent = quill.getText(index, length);
            quill.setSelection(index, length);
            quill.deleteText(index, length);
            quill.insertText(index, textContent);
            quill.setSelection(index, textContent.length);
          }
        }

        if (tooltipBox.isConnected) {
          textarea.removeEventListener('keydown', keydownHandler);
          textarea.removeEventListener('blur', saveTooltip);
          const parent = tooltipBox.parentNode;
          if (parent && parent.contains(tooltipBox)) {
            try {
              parent.removeChild(tooltipBox);
            } catch (error) {
              // Already removed
            }
          }
        }
      };

      const keydownHandler = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          saveTooltip();
        } else if (e.key === 'Escape') {
          if (isSaved) return;
          isSaved = true;

          if (tooltipBox.isConnected) {
            textarea.removeEventListener('keydown', keydownHandler);
            textarea.removeEventListener('blur', saveTooltip);
            const parent = tooltipBox.parentNode;
            if (parent && parent.contains(tooltipBox)) {
              parent.removeChild(tooltipBox);
            }
          }
        }
      };

      textarea.addEventListener('keydown', keydownHandler);
      textarea.addEventListener('blur', saveTooltip);
    }
  }, 0);
};

export const setupTooltipEditOnHover = (quillRef: React.RefObject<any>) => {
  const quill = quillRef.current;
  if (!quill) return;
  const quillContainer = quill.container as HTMLElement;

  // Reuse handlers if already created
  const anyContainer = quillContainer as any;
  if (!anyContainer._tooltipHandlers) {
    anyContainer._tooltipHandlers = {
      click: handleTooltipClick.bind(null, quill, quillContainer),
      hover: handleTooltipHover.bind(null, quill, quillContainer),
    };
  }

  const { click, hover } = anyContainer._tooltipHandlers;

  quillContainer.removeEventListener('click', click, true);
  quillContainer.removeEventListener('mouseover', hover, true);

  quillContainer.addEventListener('click', click, true);
  quillContainer.addEventListener('mouseover', hover, true);

  const handleVisibilityCheck = () => {
    checkQuillVisibility(quillContainer);
  };

  // Avoid adding multiple global listeners
  if (!anyContainer._tooltipVisibilityHandlerAttached) {
    anyContainer._tooltipVisibilityHandlerAttached = true;
    window.addEventListener('scroll', handleVisibilityCheck, true);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        closeAllTooltips();
      }
    });
  }
};

const isMultilineElement = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  const tempSpan = document.createElement('span');
  tempSpan.textContent = 'M';
  tempSpan.style.visibility = 'hidden';
  tempSpan.style.position = 'absolute';
  tempSpan.style.fontSize = window.getComputedStyle(element).fontSize;
  tempSpan.style.fontFamily = window.getComputedStyle(element).fontFamily;
  tempSpan.style.lineHeight = window.getComputedStyle(element).lineHeight;
  document.body.appendChild(tempSpan);

  const singleLineHeight = tempSpan.getBoundingClientRect().height;
  document.body.removeChild(tempSpan);

  return rect.height > singleLineHeight * 1.5;
};

const getFirstWordRectForElement = (
  element: HTMLElement,
): { top: number; left: number; width: number; height: number } | null => {
  const text = element.textContent || '';
  const words = text.trim().split(/\s+/);
  if (words.length === 0 || words[0].length === 0) {
    return null;
  }

  const firstWord = words[0];
  const firstWordIndex = text.indexOf(firstWord);

  const range = document.createRange();
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

  let charCount = 0;
  let textNode: Text | null = null;
  let startOffset = 0;
  let endOffset = 0;

  while ((textNode = walker.nextNode() as Text | null)) {
    const nodeText = textNode.textContent || '';
    const nodeLength = nodeText.length;

    if (charCount + nodeLength > firstWordIndex) {
      startOffset = firstWordIndex - charCount;
      endOffset = Math.min(startOffset + firstWord.length, nodeLength);
      range.setStart(textNode, startOffset);
      range.setEnd(textNode, endOffset);
      break;
    }

    charCount += nodeLength;
  }

  if (!range.collapsed) {
    const rect = range.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };
  }

  return null;
};

export const setupTooltipHoverForRenderer = (
  containerRef: React.RefObject<HTMLElement>,
  rtl = false,
) => {
  const container = containerRef.current;
  if (!container) {
    return () => {
      // No cleanup needed if container is not available
    };
  }

  const handleTooltipHover = (e: Event) => {
    const target = e.target as HTMLElement;
    if (
      target &&
      target.classList.contains('custom-tooltip') &&
      target.hasAttribute('data-quill-tooltip')
    ) {
      // Remove any existing hover tooltip
      const existingHover = document.body.querySelector(
        '.custom-tooltip-hover-display',
      );
      if (existingHover && existingHover.isConnected) {
        existingHover.remove();
      }

      const tooltipText = target.getAttribute('data-quill-tooltip') || '';
      if (!tooltipText) return;

      const isInSummary = isInsideSummaryContainer(target);
      const hoverTooltip = createTooltipContainer(
        'custom-tooltip-hover-display',
        isInSummary,
      );
      hoverTooltip.textContent = tooltipText;
      if (rtl) {
        hoverTooltip.setAttribute('dir', 'rtl');
      }
      addCaretToTooltip(hoverTooltip);
      document.body.appendChild(hoverTooltip);

      const targetRectViewport = getTargetRectViewportForElement(target);
      positionAndStyleTooltip(hoverTooltip, targetRectViewport);

      // Remove on mouse leave from both target and tooltip
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const removeHover = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        timeoutId = setTimeout(() => {
          if (hoverTooltip.isConnected) {
            hoverTooltip.remove();
            target.removeEventListener('mouseleave', removeHover);
            hoverTooltip.removeEventListener('mouseenter', cancelRemove);
            hoverTooltip.removeEventListener('mouseleave', removeHover);
          }
        }, 100);
      };

      const cancelRemove = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      target.addEventListener('mouseleave', removeHover);
      hoverTooltip.addEventListener('mouseenter', cancelRemove);
      hoverTooltip.addEventListener('mouseleave', removeHover);
    }
  };

  container.removeEventListener('mouseover', handleTooltipHover, true);
  container.addEventListener('mouseover', handleTooltipHover, true);

  // Cleanup function
  return () => {
    container.removeEventListener('mouseover', handleTooltipHover, true);
    const existingHover = document.body.querySelector(
      '.custom-tooltip-hover-display',
    );
    if (
      existingHover &&
      existingHover.isConnected &&
      existingHover.parentNode
    ) {
      try {
        existingHover.remove();
      } catch (error) {
        // Already removed
      }
    }
  };
};
